import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon,
  Storage as CacheIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  ClearAll as ClearAllIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useSearchOptimization from '../../hooks/useSearchOptimization';

/**
 * Composant dashboard des analytics de recherche en temps réel
 * Affiche les métriques de performance, cache, et personnalisation
 */
const SearchAnalyticsDashboard = ({ onClose, open = true }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  const {
    analytics,
    realtimeMetrics,
    cacheStats,
    personalization,
    isLoading,
    error,
    loadAnalytics,
    loadRealtimeMetrics,
    loadCacheStats,
    invalidateCache,
    clearLocalCache,
    startRealtimeMonitoring,
    stopRealtimeMonitoring,
    getLocalMetrics
  } = useSearchOptimization();

  const [isMonitoring, setIsMonitoring] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [showClearCacheDialog, setShowClearCacheDialog] = useState(false);
  const [localMetrics, setLocalMetrics] = useState(null);

  // Charger les données initiales
  useEffect(() => {
    if (open) {
      loadAnalytics(selectedTimeframe);
      loadCacheStats();
      setLocalMetrics(getLocalMetrics());
    }
  }, [open, selectedTimeframe, loadAnalytics, loadCacheStats, getLocalMetrics]);

  // Gestion du monitoring temps réel
  useEffect(() => {
    if (isMonitoring && open) {
      startRealtimeMonitoring(3000); // Toutes les 3 secondes
    } else {
      stopRealtimeMonitoring();
    }

    return () => stopRealtimeMonitoring();
  }, [isMonitoring, open, startRealtimeMonitoring, stopRealtimeMonitoring]);

  const handleToggleMonitoring = () => {
    setIsMonitoring(!isMonitoring);
  };

  const handleTimeframeChange = (timeframe) => {
    setSelectedTimeframe(timeframe);
    loadAnalytics(timeframe);
  };

  const handleClearCache = async () => {
    try {
      await invalidateCache();
      clearLocalCache();
      setLocalMetrics(getLocalMetrics());
      setShowClearCacheDialog(false);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num?.toString() || '0';
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getStatusColor = (value, threshold) => {
    if (value < threshold) return 'success';
    if (value < threshold * 1.5) return 'warning';
    return 'error';
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AnalyticsIcon color="primary" />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {t('search.analytics.dashboard.title')}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isMonitoring}
              onChange={handleToggleMonitoring}
              color="primary"
            />
          }
          label={t('search.analytics.realtime')}
        />
        <IconButton onClick={() => loadAnalytics(selectedTimeframe)} disabled={isLoading}>
          <RefreshIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Sélecteur de période */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('search.analytics.timeframe')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['1h', '6h', '24h', '7d', '30d'].map((timeframe) => (
              <Chip
                key={timeframe}
                label={timeframe}
                onClick={() => handleTimeframeChange(timeframe)}
                color={selectedTimeframe === timeframe ? 'primary' : 'default'}
                variant={selectedTimeframe === timeframe ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Métriques temps réel */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SpeedIcon color="primary" />
                    <Typography variant="h6">
                      {t('search.analytics.realtime_metrics')}
                    </Typography>
                    {isMonitoring && (
                      <Chip
                        label="LIVE"
                        color="success"
                        size="small"
                        sx={{ 
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                            '100%': { opacity: 1 }
                          }
                        }}
                      />
                    )}
                  </Box>

                  {realtimeMetrics && (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.active_searches')}
                        </Typography>
                        <Typography variant="h4" color="primary">
                          {realtimeMetrics.activeSearches || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.avg_response_time')}
                        </Typography>
                        <Typography variant="h4" color={getStatusColor(realtimeMetrics.avgResponseTime, 500)}>
                          {formatDuration(realtimeMetrics.avgResponseTime || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.cache_hit_rate')}
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {((realtimeMetrics.cacheHitRate || 0) * 100).toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.error_rate')}
                        </Typography>
                        <Typography variant="h4" color={getStatusColor((realtimeMetrics.errorRate || 0) * 100, 5)}>
                          {((realtimeMetrics.errorRate || 0) * 100).toFixed(1)}%
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {!realtimeMetrics && !isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      {t('search.analytics.no_realtime_data')}
                    </Typography>
                  )}

                  {isLoading && <LinearProgress sx={{ mt: 2 }} />}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Statistiques de cache */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CacheIcon color="secondary" />
                    <Typography variant="h6">
                      {t('search.analytics.cache_stats')}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ClearAllIcon />}
                      onClick={() => setShowClearCacheDialog(true)}
                      color="warning"
                    >
                      {t('search.analytics.clear_cache')}
                    </Button>
                  </Box>

                  {cacheStats && (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.cache_entries')}
                        </Typography>
                        <Typography variant="h4" color="secondary">
                          {formatNumber(cacheStats.totalEntries || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.cache_memory')}
                        </Typography>
                        <Typography variant="h4" color="secondary">
                          {cacheStats.memoryUsage ? `${(cacheStats.memoryUsage / 1024 / 1024).toFixed(1)}MB` : '0MB'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('search.analytics.cache_hit_rate')}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(cacheStats.hitRate || 0) * 100}
                          color="success"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {((cacheStats.hitRate || 0) * 100).toFixed(1)}%
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {localMetrics && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        {t('search.analytics.local_metrics')}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Cache local
                          </Typography>
                          <Typography variant="body2">
                            {localMetrics.cacheSize} entrées
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Historique
                          </Typography>
                          <Typography variant="body2">
                            {localMetrics.historySize} recherches
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Recherches
                          </Typography>
                          <Typography variant="body2">
                            {localMetrics.searchCount}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Analytics détaillés */}
          {analytics && (
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <TimelineIcon color="info" />
                      <Typography variant="h6">
                        {t('search.analytics.detailed_analytics')}
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.total_searches')}
                        </Typography>
                        <Typography variant="h4" color="info">
                          {formatNumber(analytics.totalSearches || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.unique_users')}
                        </Typography>
                        <Typography variant="h4" color="info">
                          {formatNumber(analytics.uniqueUsers || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('search.analytics.avg_results_per_search')}
                        </Typography>
                        <Typography variant="h4" color="info">
                          {(analytics.avgResultsPerSearch || 0).toFixed(1)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {analytics.topQueries && analytics.topQueries.length > 0 && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          {t('search.analytics.top_queries')}
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{t('search.analytics.query')}</TableCell>
                                <TableCell align="right">{t('search.analytics.count')}</TableCell>
                                <TableCell align="right">{t('search.analytics.avg_results')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {analytics.topQueries.slice(0, 5).map((query, index) => (
                                <TableRow key={index}>
                                  <TableCell>{query.query}</TableCell>
                                  <TableCell align="right">{query.count}</TableCell>
                                  <TableCell align="right">{query.avgResults?.toFixed(1) || 0}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>

      {/* Dialog de confirmation pour vider le cache */}
      <Dialog
        open={showClearCacheDialog}
        onClose={() => setShowClearCacheDialog(false)}
      >
        <DialogTitle>{t('search.analytics.clear_cache_confirm_title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('search.analytics.clear_cache_confirm_message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearCacheDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleClearCache} color="warning" variant="contained">
            {t('search.analytics.clear_cache')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SearchAnalyticsDashboard;
